import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment-timezone';
import { BsDatepickerConfig, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { EMPTY, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ES_Zones, UK_Zones, BARCELONA_ZONE_ID } from 'src/app/core/constants/ZonesIds';
import { LocalStorageKeys } from 'src/app/core/enums/LocalStorageKeys';
import { ProductKey, ITV_PRODUCTS } from 'src/app/core/enums/ProductKey';
import { ConcatenatedHour, Hour, HoursAvailabilityResponse } from 'src/app/pages/new-service/interfaces/Hour';
import { LocationInfo } from 'src/app/pages/new-service/interfaces/SummaryData';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { SharedFunnelService } from 'src/app/shared/functions/shared-functions';

export interface ServiceDateStardEnd {
  serviceStartDate: string;
  serviceEndDate: string;
}

type DateHourFormatted = Readonly<{ date: string; hour: string }>;

@Component({
  selector: 'app-date-hour-service-form',
  templateUrl: './date-hour-service-form.component.html',
  styleUrls: ['./date-hour-service-form.component.scss'],
})
export class DateHourServiceFormComponent implements OnInit, OnChanges, OnDestroy {
  readonly ProductKey = ProductKey;
  readonly ES_Zones = ES_Zones;

  @Input() onlySpecificHour = false;
  @Input() productId?: string;
  @Input() productKey?: ProductKey;
  @Input() zoneId?: string;
  @Input() startDate = '';
  @Input() endDate = '';
  @Input() applyReplacementLogic = false;
  @Input() productBlockedDates: ReadonlyArray<Date> = [];
  @Input() mainServiceTimeSlot?: LocationInfo;
  @Input() isSecondaryService = false;
  @Input() enableAvailabilitySubtraction = false;

  @Output() localtionInfoEmitter = new EventEmitter<LocationInfo>();
  @Output() datesEmitter = new EventEmitter<ServiceDateStardEnd>();

  calendarDisabledDays: number[] = [6, 0];
  today = new Date();
  bsConfig: Partial<BsDatepickerConfig> = {};

  isSpecificHoursDropdownOpen = false;
  showDropdown = false;
  openForm = false;

  noAvailableHours = false;
  noAvailableEndHours = false;
  warningMessage = '';

  isSpecificHours = false;

  selectedDate?: Date;

  availableHours: ConcatenatedHour[] = [];
  selectedHourPair: ConcatenatedHour | null = null;

  availableStartHours: HoursAvailabilityResponse[] = [];
  availableEndHours: HoursAvailabilityResponse[] = [];
  selectedStartHour: HoursAvailabilityResponse | null = null;
  selectedEndHour: HoursAvailabilityResponse | null = null;

  formattedStartTime = '';
  formattedEndTime = '';
  currentAvailableHours: HoursAvailabilityResponse[] = [];
  private availabilityLoaded = false;

  private readonly destroy$ = new Subject<void>();

  private readonly replacementVehicleAvailableProductKeys: ReadonlyArray<ProductKey> = [
    ProductKey.Transfer,
    ProductKey.Transit,
    ProductKey.Collect,
    ProductKey.Return,
  ];

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly localeService: BsLocaleService,
    private readonly sharedFunnelService: SharedFunnelService,
    private readonly translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.configureDisabledDays();
    this.configureDefaultModeByZone();
    this.setDatePickerConfig();
    this.initializeDateAndTimeFromSummary();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productBlockedDates']) {
      this.refreshDatepickerConfig();
      if (this.selectedDate && this.isBlockedDate(this.selectedDate)) {
        this.selectedDate = undefined;
        this.resetTimeSelections();
        this.noAvailableHours = true;
        this.noAvailableEndHours = true;
      }
    }

    if (changes['applyReplacementLogic'] && !changes['applyReplacementLogic'].firstChange) {
      this.initializeDateAndTimeFromSummary();
    }

    if ((changes['startDate'] && !changes['startDate'].firstChange) || (changes['endDate'] && !changes['endDate'].firstChange)) {
      this.initializeDateAndTimeFromSummary();
    }

    if (changes['mainServiceTimeSlot'] && this.isSecondaryService && this.enableAvailabilitySubtraction && this.mainServiceTimeSlot && !this.availabilityLoaded) {
      this.initializeDateAndTimeFromSummary();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.availabilityLoaded = false;
  }

  private setDatePickerConfig(): void {
    const savedLanguage = this.localStorageService.get<string>(LocalStorageKeys.Language);
    this.localeService.use(savedLanguage || 'es');

    this.bsConfig = {
      dateInputFormat: 'DD/MM/YYYY',
      containerClass: 'theme-default',
      showWeekNumbers: false,
      daysDisabled: this.calendarDisabledDays,
      minDate: this.today,
      datesDisabled: this.getNormalizedBlockedDates(),
      adaptivePosition: true,
      isAnimated: true,
    };
  }

  private refreshDatepickerConfig(): void {
    this.bsConfig = {
      ...this.bsConfig,
      datesDisabled: this.getNormalizedBlockedDates(),
    };
  }

  getNormalizedBlockedDates(): Date[] {
    return (this.productBlockedDates ?? [])
      .map((d) => new Date(d))
      .filter((d) => !Number.isNaN(d.getTime()))
      .map((d) => {
        d.setHours(0, 0, 0, 0);
        return d;
      });
  }

  private toDateKey(d: Date): string {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, '0');
    const day = String(x.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private isBlockedDate(date: Date): boolean {
    const key = this.toDateKey(date);
    return this.getNormalizedBlockedDates().some((b) => this.toDateKey(b) === key);
  }

  private initializeDateAndTimeFromSummary(): void {
    if (this.isSecondaryService && this.enableAvailabilitySubtraction) {
      return;
    }

    if (!this.startDate || !this.endDate) return;

    const start = this.getDateFromSummary(this.startDate);

    if (this.isBlockedDate(start)) {
      this.selectedDate = undefined;
      this.resetTimeSelections();
      this.noAvailableHours = true;
      this.noAvailableEndHours = true;
      return;
    }

    this.selectedDate = start;
    this.setFormattedTimesFromSummary();
    this.isSpecificHours = this.detectThirtyMinuteSlots([this.formattedStartTime, this.formattedEndTime]);

    this.loadAvailability(start)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (hours) => this.handleAvailability(hours),
        error: (err: unknown) => {
          console.error('[DateHourServiceForm] Error obteniendo disponibilidad', err);
          this.noAvailableHours = true;
          this.noAvailableEndHours = true;
        },
      });
  }

  onPickupDateChange(date: Date): void {
    if (!date) return;

    this.availabilityLoaded = false;

    if (this.isBlockedDate(date)) {
      this.selectedDate = undefined;
      this.resetNoAvailability();
      return;
    }

    this.selectedDate = date;
    this.noAvailableEndHours = false;
    this.resetTimeSelections();
    this.getProductAvailability(date);
  }

  private resetNoAvailability(): void {
    this.resetTimeSelections();
    this.noAvailableHours = true;
    this.noAvailableEndHours = true;
  }

  private getProductAvailability(selectedDate: Date): void {
    if (!this.productId) return;

    this.noAvailableHours = false;

    this.loadAvailability(selectedDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (hours) => { this.handleAvailability(hours); },
        error: (err: unknown) => {
          console.error('[DateHourServiceForm] Error loading product availability', err);
          this.noAvailableHours = true;
          this.noAvailableEndHours = true;
        },
      });
  }

  private loadAvailability(date: Date): Observable<Hour[]> {
    if (!this.productId) return EMPTY;
    return this.sharedFunnelService.getProductAvailabilityShared(this.productId, date, this.isSpecificHours, false);
  }

  private handleAvailability(hours: Hour[]): void {
    this.openForm = false;

    if (!hours?.length) {
      this.noAvailableHours = true;
      this.noAvailableEndHours = true;
      return;
    }

    this.noAvailableHours = false;

    const processedHours = (this.isSecondaryService && this.enableAvailabilitySubtraction && this.mainServiceTimeSlot)
      ? this.subtractMainServiceAvailability(hours)
      : hours;

    if (this.isTransit()) {
      this.selectPickupTime(processedHours[0]);
      this.selectEndHour(processedHours[processedHours.length - 1]);
      return;
    }

    const processed = this.applyReplacementVehicleRules(processedHours);

    if (this.isSpecificHours) {
      this.processSpecificHoursMode(processed);
    } else {
      this.processRangeHoursMode(processed);
    }

    this.emitDates();
  }

  toggleSpecificHoursDropdown(): void {
    this.isSpecificHoursDropdownOpen = !this.isSpecificHoursDropdownOpen;
  }

  selectSpecificHours(value: boolean): void {
    this.isSpecificHoursDropdownOpen = false;
    if (value === this.isSpecificHours) return;

    this.isSpecificHours = value;
    this.resetTimeSelections();

    if (this.selectedDate) {
      this.getProductAvailability(this.selectedDate);
    }
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  selectHour(hour: ConcatenatedHour): void {
    if (hour.disabled) return;

    this.selectedHourPair = hour;
    this.showDropdown = false;
    this.openForm = true;
    this.emitDates();
  }

  private isNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }

  shouldShowQuota(hour: ConcatenatedHour): boolean {
    if (hour.disabled) return false;
    const quota = (hour as { quota?: unknown }).quota;
    return this.isNumber(quota) && quota < 8;
  }

  getQuotaText(hour: ConcatenatedHour): string {
    const quota = (hour as { quota?: unknown }).quota;
    if (!this.isNumber(quota) || quota <= 0) return '';
    return `${quota} ${this.translateService.instant('availables')}`;
  }

  selectPickupTime(hour: HoursAvailabilityResponse): void {
    this.selectedStartHour = hour;
    this.handleStartTimeChange(hour);
    this.emitDates();
  }

  selectEndHour(hour: HoursAvailabilityResponse): void {
    this.selectedEndHour = hour;
    this.openForm = true;
    this.emitDates();
  }

  private resetTimeSelections(): void {
    this.selectedHourPair = null;
    this.selectedStartHour = null;
    this.selectedEndHour = null;
    this.availableEndHours = [];
    this.showDropdown = false;
    this.openForm = false;
  }

  private processSpecificHoursMode(hours: Hour[]): void {
    this.availableHours = this.sharedFunnelService.transformHours(hours);

    const enabled = this.availableHours.filter((x) => !x.disabled);

    const match = this.formattedStartTime
      ? enabled.find((h) => h.start.Text?.includes(this.formattedStartTime))
      : undefined;

    this.selectedHourPair = match ?? null;
    this.openForm = Boolean(match);
  }

  private processRangeHoursMode(hours: Hour[]): void {
    this.currentAvailableHours = [...hours];

    const minGapHours = !!this.zoneId && ES_Zones.includes(this.zoneId) ? 2 : 3;
    this.availableStartHours = [...hours].slice(0, -minGapHours);
    this.availableEndHours = [...hours];

    this.noAvailableHours = this.availableStartHours.length === 0 && this.availableEndHours.length === 0;

    const startHour = this.availableStartHours.find((h) => h.Text === this.formattedStartTime) ?? null;
    const endHour = this.availableEndHours.find((h) => h.Text === this.formattedEndTime) ?? null;

    if (startHour) {
      this.selectedStartHour = startHour;
      this.handleStartTimeChange(startHour);
    }

    if (endHour) {
      this.selectedEndHour = endHour;
    }

    this.openForm = Boolean(startHour && endHour);
  }

  private applyReplacementVehicleRules(hours: Hour[]): Hour[] {
    if (!this.productKey) return hours;
    if (!this.replacementVehicleAvailableProductKeys.includes(this.productKey)) return hours;
    if (!this.applyReplacementLogic) return hours;

    const isReturn = this.productKey === ProductKey.Return;

    const result = hours.map((h) => ({ ...h }));

    const limit = this.isSpecificHours ? 2 : 1;
    const enabledHours = result.filter((h) => !h.Disabled);

    const target = isReturn ? enabledHours.slice(-limit) : enabledHours.slice(0, limit);
    target.forEach((h) => (h.Disabled = true));

    return result;
  }

  private handleStartTimeChange(event: HoursAvailabilityResponse | null): void {
    if (!event?.Text || this.currentAvailableHours.length === 0) return;

    const startTime = this.parseTime(event.Text);
    const minEndTime = this.addHours(startTime, 2);

    const lastText = this.currentAvailableHours[this.currentAvailableHours.length - 1]?.Text;
    if (!lastText) return;

    const lastAvailableTime = this.parseTime(lastText);

    const endHours = this.filterEndHours(this.currentAvailableHours, minEndTime, lastAvailableTime);

    this.availableEndHours = endHours;
    this.noAvailableEndHours = endHours.length === 0;
    this.selectedEndHour = null;

    this.warningMessage = this.translateService.instant('changePickupTimeOrContactSupport');
  }

  private filterEndHours(
    hours: HoursAvailabilityResponse[],
    minEndTime: Date,
    lastAvailableTime: Date
  ): HoursAvailabilityResponse[] {
    const isSpain = !!this.zoneId && ES_Zones.includes(this.zoneId);
    return hours.filter((hour) => {
      const text = hour.Text;
      if (!text) return false;

      const hourTime = this.parseTime(text);
      const meetsMinGap = isSpain ? hourTime >= minEndTime : hourTime > minEndTime;
      return meetsMinGap && hourTime <= lastAvailableTime;
    });
  }

  private parseTime(time: string): Date {
    return new Date(`2000-01-01T${time}`);
  }

  private addHours(date: Date, hours: number): Date {
    return new Date(date.getTime() + hours * 60 * 60 * 1000);
  }

  private getDateFromSummary(dateString: string): Date {
    const [datetimePart, tzPart] = dateString.split(' ');
    return tzPart ? moment.tz(datetimePart, tzPart).toDate() : moment(datetimePart).toDate();
  }

  private setFormattedTimesFromSummary(): void {
    const start = this.formatFromSummary(this.startDate);
    const end = this.formatFromSummary(this.endDate);

    this.formattedStartTime = start.hour;
    this.formattedEndTime = end.hour;
  }

  private formatFromSummary(input: string): DateHourFormatted {
    const [rawDateTime, tzPart] = input.split(' ');
    const fixedDateTime = rawDateTime.replace(/([+-]\d{2})$/, '$1:00');
    const parsedDate = tzPart
      ? moment.tz(fixedDateTime, tzPart)
      : moment.parseZone(fixedDateTime);
    if (!parsedDate.isValid()) {
      console.warn('Invalid date received:', input);
      return { date: '', hour: '' };
    }
    return {
      date: parsedDate.format('DD/MM/YYYY'),
      hour: parsedDate.format('HH:mm'),
    };
  }
  private detectThirtyMinuteSlots(hours: string[]): boolean {
    const toMinutes = (time: string): number | null => {
      if (!time || !time.includes(':')) return null;
      const [h, m] = time.split(':').map(Number);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };

    const first = toMinutes(hours[0] ?? '');
    const second = toMinutes(hours[1] ?? '');

    if (first === null || second === null) return false;
    return Math.abs(second - first) === 30;
  }

  private configureDefaultModeByZone(): void {
    if (this.isTransit()) {
      this.isSpecificHours = false;
      return;
    }

    // For Barcelona with ITV services, force range hours mode (no specific hours)
    if (this.zoneId === BARCELONA_ZONE_ID && this.productKey && ITV_PRODUCTS.includes(this.productKey)) {
      this.isSpecificHours = false;
      return;
    }
  }

  private configureDisabledDays(): void {
    if (this.zoneId && UK_Zones.includes(this.zoneId)) {
      this.calendarDisabledDays = [];
    } else {
      this.calendarDisabledDays = [6, 0];
    }
  }

  isTransit(): boolean {
    return this.productKey === ProductKey.Transit;
  }

  shouldShowHourModeSelector(): boolean {
    // Hide the hour mode selector (range vs specific hours) for Barcelona with ITV services
    if (this.zoneId === BARCELONA_ZONE_ID && this.productKey && ITV_PRODUCTS.includes(this.productKey)) {
      return false;
    }
    return true;
  }

  isValet(): boolean {
    return this.productKey === ProductKey.ValetTrainStation;
  }

  isTyre(): boolean {
    return this.productKey === ProductKey.Tyres;
  }

  private subtractMainServiceAvailability(hours: Hour[]): Hour[] {
    if (!this.mainServiceTimeSlot?.serviceStartDate || !this.mainServiceTimeSlot?.serviceEndDate) {
      return hours;
    }

    const mainStartTime = this.extractTimeFromKey(this.mainServiceTimeSlot.serviceStartDate);
    const mainEndTime = this.extractTimeFromKey(this.mainServiceTimeSlot.serviceEndDate);

    if (!mainStartTime || !mainEndTime) {
      return hours;
    }

    const processedHours = hours.map(hour => {
      if (hour.Disabled) {
        return { ...hour };
      }

      const hourStartTime = this.extractTimeFromKey(hour.Key);

      if (!hourStartTime) return { ...hour };

      const hasOverlap = this.hasTimeOverlap(hourStartTime, mainStartTime, mainEndTime);

      if (hasOverlap) {
        const newHour = { ...hour };

        if (newHour.Quota && newHour.Quota > 0) {
          newHour.Quota = newHour.Quota - 1;
          if (newHour.Quota === 0) {
            newHour.Disabled = true;
          }
        } else {
          newHour.Disabled = true;
        }

        return newHour;
      }

      return { ...hour };
    });

    return processedHours;
  }

  private extractTimeFromKey(key: string): string | null {
    try {
      const [datetimePart] = key.split(' ');

      let normalizedDatetime = datetimePart;
      if (datetimePart.match(/T\d{2}:\d{2}:\d{2}[+-]\d{2}$/)) {
        normalizedDatetime = datetimePart + ':00';
      }

      const date = new Date(normalizedDatetime);

      if (isNaN(date.getTime())) {
        return null;
      }

      const timeString = date.toTimeString().substring(0, 5);
      return timeString;
    } catch (error) {
      console.error('[DateHourServiceForm] Error extracting time from key:', error, key);
      return null;
    }
  }

  private hasTimeOverlap(hourStart: string, mainStart: string, mainEnd: string): boolean {
    const hourStartMinutes = this.timeToMinutes(hourStart);
    const hourEndMinutes = hourStartMinutes + 30;

    const mainStartMinutes = this.timeToMinutes(mainStart);
    const mainEndMinutes = this.timeToMinutes(mainEnd);

    const hasOverlap = hourStartMinutes < mainEndMinutes && hourEndMinutes > mainStartMinutes;

    return hasOverlap;
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private emitDates(): void {
    if (this.isSpecificHours) {
      const locationInfo = this.selectedHourPair
        ? this.sharedFunnelService.handleTimeSlotChange(this.selectedHourPair)
        : ({ serviceStartDate: '', serviceEndDate: '' } as LocationInfo);

      this.localtionInfoEmitter.emit(locationInfo);

      this.datesEmitter.emit({
        serviceStartDate: locationInfo.serviceStartDate ?? '',
        serviceEndDate: locationInfo.serviceEndDate ?? '',
      });

      return;
    }

    const locationInfo: LocationInfo = {
      serviceStartDate: this.selectedStartHour?.Key,
      formattedStartTime: this.selectedStartHour?.Text,
      serviceEndDate: this.selectedEndHour?.Key,
      formattedEndTime: this.selectedEndHour?.Text,
    };

    this.localtionInfoEmitter.emit(locationInfo);

    this.datesEmitter.emit({
      serviceStartDate: locationInfo.serviceStartDate ?? '',
      serviceEndDate: locationInfo.serviceEndDate ?? '',
    });
  }
}